import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services";
import Loader from "../../components/Loader";
import Empty from "../../components/Empty";
import ErrorMessage from "../../components/ErrorMessage";
import { Colors } from "../../utils/colors";
import { Fonts } from "../../utils/fonts";
import type { IPublicationsItem } from "../../services/types";
import { isAtMostMobile, MEDIA_QUERIES } from "../../constants/breakpoints";
import { LAYOUT, getShelfBottom } from "../../constants/layout";
import { Z_INDEX } from "../../constants/zIndex";
import { useAvailableWidth, useSidebarWidth } from "../../hooks/useResponsive";

import shelfPng from "../../assets/shelf.png";
import { FontSizes } from "../../assets/fonts/fontSizes";

type Props = { categoryId: string; titleOverride?: string };

function calculateColumns(availableWidth: number): number {
  // Calculate how many base-sized cards can fit with preferred gap
  const maxPossibleCols = Math.floor(
    (availableWidth + LAYOUT.publicationsBaseCard.gap) /
      (LAYOUT.publicationsBaseCard.width + LAYOUT.publicationsBaseCard.gap),
  );

  // Clamp to min/max range
  const cols = Math.max(
    LAYOUT.publicationsColumns.min,
    Math.min(LAYOUT.publicationsColumns.max, maxPossibleCols),
  );

  return cols;
}

export default function PublicationsShelf({
  categoryId,
  titleOverride,
}: Props) {
  const [docs, setDocs] = useState<IPublicationsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const navigate = useNavigate();

  // Get available width (window width - sidebar)
  const availableWidth = useAvailableWidth();
  const sidebarWidth = useSidebarWidth();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(false);

        const data = await API.getPublications(categoryId);
        if (!Array.isArray(data)) throw new Error("Bad publications payload");

        setDocs(data);
      } catch (err) {
        console.error("Publications fetch failed:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [categoryId]);

  const layout = useMemo(() => {
    // Use smaller padding on mobile (60px to account for menu button)
    const isMobile = isAtMostMobile(availableWidth);
    const sidePadding = LAYOUT.documentsSidePadding;
    const totalSidePadding = isMobile ? sidePadding * 2 : sidePadding * 4;
    const innerAvailable = Math.max(0, availableWidth - totalSidePadding);

    // Fallback while measuring
    if (!innerAvailable) {
      return {
        perRow: LAYOUT.publicationsColumns.default,
        cardW: LAYOUT.publicationsBaseCard.width,
        cardH: LAYOUT.publicationsBaseCard.height,
        gap: LAYOUT.publicationsBaseCard.gap,
        shelfW: 0,
      };
    }

    // Calculate dynamic column count
    const cols = calculateColumns(innerAvailable);

    // Always use the optimal gap
    const gap = LAYOUT.publicationsBaseCard.gap;
    const totalGap = gap * (cols - 1);

    // Scale cards to fill available width perfectly
    const cardW = Math.floor((innerAvailable - totalGap) / cols);
    const scale = cardW / LAYOUT.publicationsBaseCard.width;
    const cardH = Math.floor(LAYOUT.publicationsBaseCard.height * scale);

    // Calculate actual row width (what the cards will take up)
    const actualRowWidth = cols * cardW + totalGap;

    // Shelf extends beyond cards with padding on both sides
    const shelfW = actualRowWidth + sidePadding * 2;

    // Use centralized shelf bottom calculation
    const shelfBottom = getShelfBottom(innerAvailable);

    return {
      perRow: cols,
      cardW: Math.max(60, cardW),
      cardH: Math.max(80, cardH),
      gap,
      shelfW,
      shelfBottom,
    };
  }, [availableWidth]);

  const rows: IPublicationsItem[][] = useMemo(() => {
    const out: IPublicationsItem[][] = [];
    for (let i = 0; i < docs.length; i += layout.perRow) {
      out.push(docs.slice(i, i + layout.perRow));
    }
    return out;
  }, [docs, layout.perRow]);

  const openDoc = (doc: IPublicationsItem) => {
    navigate(`/documents/${doc.id}`, { state: { publication: doc } });
  };

  if (loading) return <Loader />;
  if (error) return <ErrorMessage />;
  if (!docs.length) return <Empty />;

  return (
    <div style={styles.wrapper}>
      <style>{`
        ${MEDIA_QUERIES.tablet} {
          .publications-header {
            left: 0 !important;
            padding-left: 60px !important;
            padding-right: 20px !important;
          }
        }
      `}</style>

      {/* Title + Count */}
      <div
        style={{ ...styles.header, left: sidebarWidth }}
        className="publications-header"
      >
        <div style={styles.title}>
          {(titleOverride ?? "LATEST").toUpperCase()}
        </div>
        <div style={styles.countContainer}>
          <div style={styles.count}>{docs.length}</div>
        </div>
      </div>

      {/* Rows */}
      <div style={styles.listContent}>
        {rows.map((row, i) => (
          <div key={`row-${i}`} style={styles.rowWrap}>
            <div
              style={{
                ...styles.rowInner,
                paddingLeft: LAYOUT.documentsSidePadding,
                paddingRight: LAYOUT.documentsSidePadding,
              }}
            >
              <div
                style={{
                  ...styles.row,
                  gap: layout.gap,
                }}
              >
                {row.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => openDoc(doc)}
                    style={{
                      ...styles.cardWrap,
                      width: layout.cardW,
                      height: layout.cardH,
                    }}
                  >
                    <img
                      src={doc.thumbnailUrl}
                      alt={doc.name}
                      style={styles.cardImage}
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Shelf behind with padding around cards */}
            <div
              style={{
                ...styles.shelfShadow,
                width: layout.shelfW || "100%",
                bottom: layout.shelfBottom,
              }}
            >
              <img
                src={shelfPng}
                alt=""
                style={styles.shelfImg}
                draggable={false}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: { width: "100%", position: "relative" },
  header: {
    position: "fixed",
    top: LAYOUT.topbarHeight,
    right: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 16,
    paddingBottom: 16,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    zIndex: Z_INDEX.header,
  },
  title: {
    fontSize: FontSizes.lg,
    fontWeight: Fonts.SemiBold,
    color: Colors.White,
    marginRight: 10,
    fontFamily: "Inter",
  },
  countContainer: {
    height: 24,
    width: 24,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.SecondaryColor,
    borderRadius: 12,
  },
  count: {
    color: Colors.Black,
    fontSize: FontSizes.sm,
    fontWeight: Fonts.SemiBold,
    fontFamily: "Inter",
  },
  listContent: {
    paddingBottom: 24,
    paddingTop: 50,
  },
  rowWrap: {
    position: "relative",
    paddingTop: 8,
    marginBottom: 56,
  },
  rowInner: {
    position: "relative",
    zIndex: 1,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    position: "relative",
    zIndex: 1,
    flexWrap: "nowrap",
  },

  shelfShadow: {
    position: "absolute",
    left: 0,
    bottom: -50,
    zIndex: Z_INDEX.shelfShadow,
    pointerEvents: "none",
  },
  shelfImg: {
    width: "100%",
    height: "auto",
    display: "block",
    userSelect: "none",
  },
  cardWrap: {
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: Colors.cardBackground,
    border: `2px solid ${Colors.borderCard}`,
    padding: 0,
    cursor: "pointer",
    transition: "transform 120ms ease",
    flex: "0 0 auto",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
};
